import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { CheckCircle, SpinnerGap, WarningCircle, ArrowRight } from "@phosphor-icons/react";

export default function BillingSuccessPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tier, setTier] = useState("");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const resp = await api.get(`/stripe/checkout-status/${sessionId}`);
        const d = resp.data;
        setTier(d.tier || "");
        if (d.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (d.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts++;
        if (attempts < 8) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        attempts++;
        if (attempts < 8) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [sessionId, api]);

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center px-4" data-testid="billing-success-page">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full text-center space-y-6">
        {status === "checking" && (
          <>
            <SpinnerGap size={48} className="animate-spin text-[#FF4400] mx-auto" />
            <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[var(--text-primary)]">Processing payment...</h1>
            <p className="text-sm text-[var(--text-muted)]">This usually takes just a moment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
              <CheckCircle size={40} weight="fill" className="text-green-500" />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[var(--text-primary)]">You're upgraded!</h1>
            <p className="text-sm text-[var(--text-secondary)]">
              Your account is now on the <span className="font-semibold text-[#FF4400] capitalize">{tier}</span> plan. All features are unlocked.
            </p>
            <button
              onClick={() => navigate("/generate")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#FF4400] text-white font-semibold hover:bg-[#E63E00] transition-all"
              data-testid="billing-success-continue"
            >
              Continue to Dashboard <ArrowRight size={16} />
            </button>
          </>
        )}
        {(status === "error" || status === "timeout" || status === "expired") && (
          <>
            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
              <WarningCircle size={40} weight="fill" className="text-amber-500" />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[var(--text-primary)]">
              {status === "expired" ? "Session expired" : "Something went wrong"}
            </h1>
            <p className="text-sm text-[var(--text-muted)]">
              {status === "expired" ? "Your checkout session expired. Please try again." : "We couldn't verify your payment. If you were charged, your account will be updated automatically."}
            </p>
            <button onClick={() => navigate("/pricing")} className="text-sm text-[#FF4400] hover:underline">
              Back to Pricing
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
